import { useCallback } from 'react'
import { Form, Field } from 'react-final-form'
import validate from 'validate.js'
import { InputField, Button, CheckboxField, Icon } from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { trackEmailCaptured } from '../../libs/client/marketing/trackEmailCaptured'
import classes from './ModalEmailCapture.module.scss'

interface ModalEmailCaptureProps {
  trackProps: {
    campaign: string
    contentType: string
    location: string
    slug: string
  }
  className?: string
  buttonType?: 'primary' | 'secondary' | 'tertiary'
  onEmailCaptured?: () => void
}

export const ModalEmailCapture = ({
  className = '',
  buttonType = 'primary',
  trackProps,
  onEmailCaptured,
}: ModalEmailCaptureProps) => {
  const { t } = useTranslation('@mc/next')

  const onSubmit = useCallback(
    async ({ email }: { email: string }) => {
      await trackEmailCaptured({
        event: 'Email Captured',
        properties: {
          email,
          ...trackProps,
        },
      })
      if (onEmailCaptured) {
        onEmailCaptured()
      }
    },
    [trackProps, onEmailCaptured],
  )

  return (
    <section
      data-testid='email-capture'
      id='email-capture'
      className='mc-w-100'
    >
      <div className={className}>
        <Form
          initialValues={{ email: '', terms: false }}
          onSubmit={onSubmit}
          validate={(values) =>
            validate(
              values,
              {
                email: {
                  presence: {
                    message: t(
                      'emailCaptureBanner.form.errors.emailIsRequired',
                    ),
                  },
                  email: {
                    message: t('emailCaptureBanner.form.errors.invalidEmail'),
                  },
                },
                terms: {
                  presence: {
                    message: t('emailCaptureBanner.form.errors.terms'),
                  },
                  inclusion: {
                    within: [true],
                    message: t('emailCaptureBanner.form.errors.terms'),
                  },
                },
              },
              { fullMessages: false },
            )
          }
          render={({ handleSubmit, submitError, submitting }) => (
            <form
              className='row flex-column flex-md-row'
              onSubmit={handleSubmit}
            >
              <div className='col-md-8'>
                <Field
                  id='email'
                  autoComplete='email'
                  component={InputField}
                  name='email'
                  placeholder={t('emailCaptureBanner.form.placeholderAlt')}
                  type='email'
                  className={classes.emailInput}
                />
                {submitError && (
                  <label className='mc-text-x-small mc-text--error mc-text--bold'>
                    <Icon className='mc-icon--2' kind='error' />
                    {submitError}
                  </label>
                )}
                <Field
                  id='terms'
                  className={`mc-mt-2 align-items-center mc-text-x-small ${classes.termsCheckbox}`}
                  component={CheckboxField}
                  name='terms'
                  label={
                    <label htmlFor='terms'>
                      <Trans
                        ns='@mc/next'
                        i18nKey='emailCaptureBanner.form.agreeAlt'
                      >
                        {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                        <a
                          className='mc-text--link'
                          href={paths.privacy}
                          target='_blank'
                          rel='noreferrer'
                        />
                      </Trans>
                    </label>
                  }
                />
              </div>
              <div className='col-md-4 mc-mt-8 mc-mt-md-0'>
                <Button
                  disabled={submitting}
                  fullWidth
                  kind={buttonType}
                  type='submit'
                >
                  {t('emailCaptureBanner.form.cta')}
                </Button>
              </div>
            </form>
          )}
        />
      </div>
    </section>
  )
}
